import { useContext, useState } from 'react';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { useQueryClient } from '@tanstack/react-query';
import { AdminIntroductions } from '@platform/client';
import { AdminTableContext } from '../../AdminTable';
import {
  Control,
  Controller,
  FormState,
  SubmitHandler,
  useForm,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';
import {
  DatePicker,
  FormsInputDropdown,
  FormsMultilineInput,
  InlineFormInput,
} from '@platform/components';

type NewIntroFormData = {
  professionalId: number;
  initiationType: 'Experteer' | 'Organization';
  personalMessage: string;
  representativeId?: number | null;
  projectId?: number | null;
  scheduledSendDate?: Date;
};

export const CreateIntroductionModal = (props: { onClose: () => void }) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { control, formState, register, handleSubmit, watch } = useForm<NewIntroFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const queryClient = useQueryClient();
  const newIntroMutator = AdminIntroductions.useCreateIntroduction({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onError: () => {
        setModalStage('error');
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminIntroductions.getGetIntroductionsQueryKey({
              ...adminQueryOptionsV2,
            }),
          ],
        });
        setModalStage('success');
      },
    },
  });

  const onSubmit: SubmitHandler<NewIntroFormData> = (data) => {
    if (!data.representativeId && !data.projectId) {
      // If neither representativeId nor projectId is provided block submit
      // error message is manually shown - workaround for multiple field validation on change
      return;
    }
    newIntroMutator.mutate({
      data: {
        professionalId: data.professionalId,
        initiationType: data.initiationType,
        personalMessage: data.personalMessage,
        representativeId: data.representativeId || null,
        projectId: data.projectId || null,
        scheduledSendDate: data.scheduledSendDate?.toISOString(),
      },
    });
  };

  return (
    <AdminFormModal
      title='Create Introduction'
      onClose={props.onClose}
      modalStage={modalStage}
      submitCTA={{
        text: 'Create',
        callback: () => {
          handleSubmit(onSubmit)();
        },
      }}
    >
      <NewIntroductionForm
        control={control}
        formState={formState}
        register={register}
        watch={watch}
      />
    </AdminFormModal>
  );
};

const NewIntroductionForm = (props: {
  control: Control<NewIntroFormData>;
  formState: FormState<NewIntroFormData>;
  register: UseFormRegister<NewIntroFormData>;
  watch: UseFormWatch<NewIntroFormData>;
}) => {
  const { control, formState, register, watch } = props;
  const repId = watch('representativeId');
  const projectId = watch('projectId');
  return (
    <div className='space-y-2'>
      <FormsInputDropdown
        type='default'
        control={control}
        name='initiationType'
        placeholder='Select user type for sender'
        label='Initiation Type'
        rules={{ required: 'Initiation type is required' }}
        items={[
          { text: 'Experteer', textValue: 'Experteer' },
          { text: 'Organization', textValue: 'Organization' },
        ]}
        error={formState.errors.initiationType}
      />
      <InlineFormInput
        register={() => register('professionalId', { required: 'Pro ID is required' })}
        label='Professional ID'
        placeholder='Enter pro user id'
        errors={formState.errors.professionalId}
      />
      <div className='flex gap-4 items-end'>
        <InlineFormInput
          register={() => register('projectId')}
          label='Project ID'
          placeholder='Leave blank for custom'
          errors={undefined}
        />
        or
        <InlineFormInput
          register={() => register('representativeId')}
          label='Representative ID'
          placeholder='Enter rep user id'
          errors={undefined}
        />
      </div>
      {/* Could not find a clean solution without libraries to manage multiple field validation */}
      {formState.isSubmitted && !projectId && !repId && (
        <div className='text-left text-sm text-red-600'>Rep or project id required</div>
      )}
      {projectId && repId && (
        <div className='text-left text-sm text-gray-400'>
          Note: introduction will be created based on project rep
        </div>
      )}
      <FormsMultilineInput
        control={control}
        name='personalMessage'
        label='Personal Message'
        rules={{ required: 'Personal message is required' }}
        formsError={formState.errors.personalMessage}
      />
      <Controller
        control={control}
        name='scheduledSendDate'
        render={({ field }) => (
          <DatePicker
            label='Schedule email (leave blank to skip sending)'
            initialDate={field.value ?? undefined}
            onChange={field.onChange}
          />
        )}
      />
    </div>
  );
};
